import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { AppRegistryPage } from '@plato/app-meta/ai-meta';
import { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { MetricInfoHint } from '@plato/app-shared/components/metric-explainer';
import { usePageAiMeta } from '@plato/app-shared/hooks/use-page-ai-meta';
import { SrOrderStatus, SrSalesOrderType } from '@plato/data/starrocks/types';
import { HomeIcon } from '@plato/ui-legacy/assets/icons/home-icon';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { TabContentTableWrapper } from '@plato/ui-legacy/components/tables/tab-content-table-wrapper';
import { TableFooter } from '@plato/ui-legacy/components/tables/table-footer';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { Page } from '@plato/ui-legacy/components/ui/page';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { TabsLegacy, TabsLegacyItem } from '@plato/ui-legacy/components/ui/tabs-legacy';
import { aDateWithTheYearLastYear } from '@plato/ui-legacy/lib/date-helpers';
import { useResizeObserver } from '@plato/ui-legacy/lib/hooks/use-resize-observer';
import { DataTable } from '@plato/ui/components/data-table';
import { endOfDay, endOfMonth, formatISO, startOfMonth, startOfToday, subDays } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { PageTitle } from '../../components/page-title.tsx';
import { Can } from '../../features/acl/can.tsx';
import { useAcl } from '../../features/acl/use-acl.ts';
import { AskPlatoHomeCard } from '../../features/ai-chat/components/ask-plato-home-card.tsx';
import { useAnalytics } from '../../features/analytics/hooks/use-analytics.ts';
import { ShowMoreArrow } from '../../features/customer-detail/components/show-more-arrow.tsx';
import { DataFreshnessIndicator } from '../../features/dashboard/components/data-freshness-indicator.tsx';
import { DataStatBlock } from '../../features/dashboard/components/data-stat-block.tsx';
import { ProgressSemiCircleChart } from '../../features/dashboard/components/progress-semi-circle-chart.tsx';
import { DashboardCustomersSection } from '../../features/dashboard/sections/dashboard-customers-section.tsx';
import { DashboardTaskDataBlockItem } from '../../features/dashboard/sections/dashboard-task-data-block-item.tsx';
import { ExplorerViewChart } from '../../features/explorer/components/explorer-view-chart.tsx';
import { generateSearchParamsForExplorer } from '../../features/explorer/hooks/use-explorer-query-params.ts';
import { useOrderColumnsForListGrid } from '../../features/list-views/columns/use-order-columns-for-list-grid.tsx';
import { useQuoteColumnsForListGrid } from '../../features/list-views/columns/use-quote-columns-for-list-grid.tsx';
import { useSortQuotesOrdersGrid } from '../../features/list-views/hooks/use-sort-quotes-orders-grid.ts';
import {
    defaultQuoteOrderFilters,
    generateSearchParamsForQuotesOrders,
} from '../../features/quote-order-list/hooks/use-quote-order-list-filters.tsx';
import {
    generateSearchParamsForOpenOrders,
    generateSearchParamsForOpenQuotes,
} from '../../lib/search-params-helpers.ts';
import {
    emptyExplorerViewFilters,
    ExplorerSortBy,
    ExplorerSortOrder,
    VisibilityWindow,
} from '../../server/utils/explorer-utils.ts';
import { api } from '../../utils/api.ts';

export function useHomeAiMeta(): AppRegistryPage {
    const { t } = useTranslation();
    const orderColumns = useOrderColumnsForListGrid();
    const quoteColumns = useQuoteColumnsForListGrid();
    const columns = useMemo(() => [...orderColumns, ...quoteColumns], [orderColumns, quoteColumns]);

    return usePageAiMeta(
        {
            path: '/home',
            title: t('page_title__home', { ns: 'common' }),
            description:
                "The landing page after sign-in and the place for today's numbers. Tiles show today's " +
                '{{metric}} and order count against yesterday and month-to-date {{metric}} against the ' +
                'same month last year. The page also holds a chart of {{metric}} against last year, ' +
                'AI-recommended customers with expansion potential or a {{metric}} decline, the ' +
                "user's open tasks and today's appointments, and, where the tenant has them, short " +
                'tables of open orders and open quotes.',
        },
        columns,
    );
}

function MarginPercentageInline({
    margin,
    revenue,
    className,
}: {
    margin: number | undefined;
    revenue: number | undefined;
    className?: string;
}) {
    if (!revenue) {
        return null;
    }
    return (
        <span className={className}>
            (<MarginPercentage margin={margin} revenue={revenue} />)
        </span>
    );
}

function HomePage() {
    const { t } = useTranslation();
    const acl = useAcl();
    const {
        canReadQuotes,
        canFilterQuoteOrderByStatus,
        canReadOpenOrders,
        canReadDashboardStatsBar,
        canReadDashboardRevenueChart,
        orderSumDocumentTypes,
        ...appConfig
    } = useAppConfig();
    const navigate = useNavigate();
    const sendEvent = useAnalytics();

    const { enableAiChat, enableHomeAskPlatoCard } = appConfig;
    const showAskPlatoCard = enableAiChat && enableHomeAskPlatoCard;

    const progressContinerRef = useRef<HTMLDivElement>(null);

    const canReadStats = acl.can('list', 'Stats');
    const showDashboardStatsBar = canReadStats && canReadDashboardStatsBar;
    const showRevenueChart = canReadStats && canReadDashboardRevenueChart;

    const goToOpenQuotes = () => {
        sendEvent('home_show_all_clicked', { section: 'open_quotes' });
        void navigate({
            pathname: '/quotes',
            search: generateSearchParamsForOpenQuotes(),
        });
    };

    const goToOpenOrders = () => {
        sendEvent('home_show_all_clicked', { section: 'open_orders' });
        void navigate({
            pathname: '/orders',
            search: generateSearchParamsForOpenOrders({
                orderStatus: appConfig.openOrderStatuses,
            }),
        });
    };

    const { width: progressWidth = 0, height: progressHeight } = useResizeObserver({
        ref: progressContinerRef,
    });

    // The chart's onReady callback advances the page; when the chart is hidden it never
    // fires, so start "done" to keep the rest of the dashboard loading.
    const [isDoneTopOfFold, setIsDoneTopOfFold] = useState(!showRevenueChart);

    const quoteColumns = useQuoteColumnsForListGrid();
    const orderColumns = useOrderColumnsForListGrid();

    const {
        sorting: orderSorting,
        setSorting: setOrderSorting,
        sortingFilter: orderSortingFilter,
    } = useSortQuotesOrdersGrid();

    const {
        sorting: quoteSorting,
        setSorting: setQuoteSorting,
        sortingFilter: quoteSortingFilter,
    } = useSortQuotesOrdersGrid();

    const today = startOfToday();
    const yesterday = subDays(today, 1);
    const todayDateRange = { from: today, to: endOfDay(today) };
    const yesterdayDateRange = { from: yesterday, to: endOfDay(yesterday) };

    const baseFilter = {
        orderTypes: [SrSalesOrderType.ORDER],
    };

    const todayOrdersSearchParams = useMemo(
        () =>
            generateSearchParamsForQuotesOrders({
                ...defaultQuoteOrderFilters,
                entryDateRange: todayDateRange,
            }),
        [],
    );

    const todaySalesReportSearchParams = useMemo(
        () =>
            generateSearchParamsForExplorer({
                window: VisibilityWindow.TODAY,
                billing: SalesMetricBasis.ORDER_SUM,
            }),
        [],
    );

    const isCustomOrderSum = useMemo(
        () => orderSumDocumentTypes.some((type) => type !== 'ORDER'),
        [orderSumDocumentTypes],
    );

    const queryOptions = { enabled: showDashboardStatsBar, refetchInterval: 15000 };

    const { data: todayAggregates, isLoading: loadingDailyStats } =
        api.platoUser.getManyQuotesOrders.getAggregates.useQuery(
            { ...baseFilter, entryDateRange: todayDateRange },
            queryOptions,
        );

    const { data: yesterdayAggregates } = api.platoUser.getManyQuotesOrders.getAggregates.useQuery(
        { ...baseFilter, entryDateRange: yesterdayDateRange },
        queryOptions,
    );

    const { data: dailyTotals, isLoading: loadingDailyTotals } =
        api.platoUser.query.explorer.getDailyOrderSum.get.useQuery(undefined, queryOptions);

    const now = new Date();
    const monthDateRange = {
        from: formatISO(startOfMonth(now), { representation: 'date' }),
        to: formatISO(endOfMonth(now), { representation: 'date' }),
    };

    const monthlyRevenueSearchParams = generateSearchParamsForExplorer({
        window: VisibilityWindow.CUSTOM_DATES,
        customDateRange: monthDateRange,
        billing: SalesMetricBasis.BILLED,
    });

    const { data: monthlyRevenue, isLoading: loadingMonthlyRevenue } =
        api.platoUser.query.explorer.getExplorerSummary.getTotalWithFilters.useQuery(
            {
                window: VisibilityWindow.CUSTOM_DATES,
                customDateRange: monthDateRange,
                billingStatus: SalesMetricBasis.BILLED,
                filters: emptyExplorerViewFilters,
                sorting: { field: ExplorerSortBy.REVENUE_CURRENT, direction: ExplorerSortOrder.DESC },
                yearPeriods: 2,
            },
            { enabled: showDashboardStatsBar },
        );

    const monthlyProgressPercentage = useMemo(() => {
        if (!monthlyRevenue) {
            return undefined;
        }
        const ratio = monthlyRevenue.revenue_last_period
            ? monthlyRevenue.revenue_current / monthlyRevenue.revenue_last_period
            : 0;
        return Number(ratio.toFixed(5));
    }, [monthlyRevenue]);

    const { data: ordersSnippetData, isSuccess: isDoneOrderList } =
        api.platoUser.getManyQuotesOrders.getWithFilters.useQuery(
            {
                totalAmountNet: {},
                orderTypes: [SrSalesOrderType.ORDER],
                orderStatus: appConfig.openOrderStatuses,
                limit: 5,
                sortingFilter: orderSortingFilter,
            },
            {
                enabled: isDoneTopOfFold,
            },
        );

    const {
        data: quotesSnippetData,
        isSuccess: isDoneQuoteList,
        isLoading: isLoadingQuotes,
    } = api.platoUser.getManyQuotesOrders.getWithFilters.useQuery(
        {
            totalAmountNet: {},
            orderTypes: [SrSalesOrderType.QUOTE],
            orderStatus: [SrOrderStatus.OPEN],
            limit: 5,
            sortingFilter: quoteSortingFilter,
        },
        {
            enabled: isDoneTopOfFold,
        },
    );

    const { data: ordersAggregates, isLoading: isLoadingOrdersCount } =
        api.platoUser.getManyQuotesOrders.getAggregates.useQuery(
            {
                orderTypes: [SrSalesOrderType.ORDER],
                orderStatus: appConfig.openOrderStatuses,
            },
            {
                enabled: isDoneQuoteList && isDoneOrderList,
            },
        );

    const { data: quotesAggregates, isLoading: isLoadingQuotesCount } =
        api.platoUser.getManyQuotesOrders.getAggregates.useQuery(
            {
                orderTypes: [SrSalesOrderType.QUOTE],
                orderStatus: [SrOrderStatus.OPEN],
            },
            {
                enabled: isDoneQuoteList && isDoneOrderList,
            },
        );

    const ordersSnippet = useMemo(
        () => (ordersSnippetData ? ordersSnippetData.result : []),
        [ordersSnippetData],
    );

    const quotesSnippet = useMemo(
        () => (quotesSnippetData ? quotesSnippetData.result : []),
        [quotesSnippetData],
    );

    return (
        <Page>
            <PageTitle title={t('page_title__home')} />
            <PageHeader icon={HomeIcon} title={t('page_title__home')} subTitle={t('page_sub_title__home')} />
            <DataFreshnessIndicator />
            {showAskPlatoCard && <AskPlatoHomeCard />}
            {showDashboardStatsBar && (
                <DataStatBlock>
                    <div className="flex flex-col gap-4 sm:flex-row md:flex-3">
                        <Link
                            className="flex-1 hover:border-gray-400 hover:bg-gray-50"
                            onClick={() => {
                                sendEvent('home_kpi_tile_clicked', { tile: 'today_sales' });
                            }}
                            to={
                                isCustomOrderSum
                                    ? {
                                          pathname: '/explorer',
                                          search: todaySalesReportSearchParams,
                                      }
                                    : {
                                          pathname: '/orders',
                                          search: todayOrdersSearchParams,
                                      }
                            }
                        >
                            <DataStatBlock.Item>
                                <DataStatBlock.Title className="mt-0">
                                    <div className="pr-relaxed">
                                        {t('overview__today_sales', { ns: 'dashboard' })}
                                        <MetricInfoHint metric={SalesMetricBasis.ORDER_SUM} />
                                    </div>
                                </DataStatBlock.Title>

                                <DataStatBlock.Body isLoading={loadingDailyTotals}>
                                    <CurrencyOrEmpty>{dailyTotals?.todayTotalAmount}</CurrencyOrEmpty>
                                </DataStatBlock.Body>
                                <DataStatBlock.Footer>
                                    {dailyTotals && (
                                        <span>
                                            <CurrencyOrEmpty>
                                                {dailyTotals.yesterdayTotalAmount}
                                            </CurrencyOrEmpty>
                                            {` ${t('overview__yesterday', { ns: 'dashboard' })}`}
                                        </span>
                                    )}
                                </DataStatBlock.Footer>
                                <div className="m-0!">
                                    <ShowMoreArrow />
                                </div>
                            </DataStatBlock.Item>
                        </Link>
                        <Can I="read" a="Stats" field="total_margin">
                            <Link
                                className="flex-1 hover:border-gray-400 hover:bg-gray-50"
                                onClick={() => {
                                    sendEvent('home_kpi_tile_clicked', { tile: 'today_margin' });
                                }}
                                to={
                                    isCustomOrderSum
                                        ? {
                                              pathname: '/explorer',
                                              search: todaySalesReportSearchParams,
                                          }
                                        : {
                                              pathname: '/orders',
                                              search: todayOrdersSearchParams,
                                          }
                                }
                            >
                                <DataStatBlock.Item>
                                    <DataStatBlock.Title>
                                        <div className="pr-relaxed">
                                            <MaskedContent>
                                                {t('overview__today_margin', { ns: 'dashboard' })}
                                            </MaskedContent>
                                            <MetricInfoHint
                                                base={SalesMetricBasis.ORDER_SUM}
                                                metric="GROSS_MARGIN"
                                            />
                                        </div>
                                    </DataStatBlock.Title>
                                    <DataStatBlock.Body isLoading={loadingDailyTotals}>
                                        <MaskedContent>
                                            <CurrencyOrEmpty>{dailyTotals?.todayGrossMargin}</CurrencyOrEmpty>
                                            <MarginPercentageInline
                                                margin={dailyTotals?.todayGrossMargin}
                                                revenue={dailyTotals?.todayTotalAmount}
                                                className="ml-1 font-normal text-sm text-neutral-500"
                                            />
                                        </MaskedContent>
                                    </DataStatBlock.Body>
                                    <DataStatBlock.Footer>
                                        <MaskedContent>
                                            {dailyTotals && (
                                                <span>
                                                    <CurrencyOrEmpty>
                                                        {dailyTotals.yesterdayGrossMargin}
                                                    </CurrencyOrEmpty>
                                                    <MarginPercentageInline
                                                        margin={dailyTotals.yesterdayGrossMargin}
                                                        revenue={dailyTotals.yesterdayTotalAmount}
                                                        className="ml-1"
                                                    />
                                                    {` ${t('overview__yesterday', { ns: 'dashboard' })}`}
                                                </span>
                                            )}
                                        </MaskedContent>
                                    </DataStatBlock.Footer>
                                    <div className="m-0!">
                                        <ShowMoreArrow />
                                    </div>
                                </DataStatBlock.Item>
                            </Link>
                        </Can>
                        <Link
                            className="flex-1 hover:border-gray-400 hover:bg-gray-50"
                            onClick={() => {
                                sendEvent('home_kpi_tile_clicked', { tile: 'today_order_count' });
                            }}
                            to={{
                                pathname: '/orders',
                                search: todayOrdersSearchParams,
                            }}
                        >
                            <DataStatBlock.Item>
                                <DataStatBlock.Title>
                                    {t('overview__today_order_count', { ns: 'dashboard' })}
                                </DataStatBlock.Title>
                                <DataStatBlock.Body isLoading={loadingDailyStats}>
                                    <NumberOrEmpty>{todayAggregates?.count}</NumberOrEmpty>
                                </DataStatBlock.Body>
                                <DataStatBlock.Footer>
                                    {yesterdayAggregates && (
                                        <span>
                                            <NumberOrEmpty decimals={0}>
                                                {yesterdayAggregates.count}
                                            </NumberOrEmpty>
                                            {` ${t('overview__yesterday', { ns: 'dashboard' })}`}
                                        </span>
                                    )}
                                </DataStatBlock.Footer>
                                <div className="m-0!">
                                    <ShowMoreArrow />
                                </div>
                            </DataStatBlock.Item>
                        </Link>
                    </div>
                    <Link
                        className="flex hover:border-gray-400 hover:bg-gray-50 md:flex-2"
                        onClick={() => {
                            sendEvent('home_kpi_tile_clicked', { tile: 'monthly_sales' });
                        }}
                        to={{ pathname: '/explorer', search: monthlyRevenueSearchParams }}
                    >
                        <DataStatBlock.Item className="w-full">
                            <div className="flex flex-row">
                                <div className="flex flex-col justify-center space-y-2">
                                    <DataStatBlock.Title>
                                        <div className="pr-relaxed">
                                            {t('table_header__sales', { ns: 'dashboard' })}{' '}
                                            <DateText
                                                format={{
                                                    year: 'numeric',
                                                    month: 'short',
                                                }}
                                            >
                                                {new Date()}
                                            </DateText>
                                            <MetricInfoHint metric={SalesMetricBasis.BILLED} />
                                        </div>
                                    </DataStatBlock.Title>
                                    <DataStatBlock.Body
                                        className="flex items-center space-x-1 "
                                        isLoading={loadingMonthlyRevenue}
                                    >
                                        <span>
                                            <CurrencyOrEmpty>
                                                {monthlyRevenue?.revenue_current}
                                            </CurrencyOrEmpty>
                                        </span>

                                        <div className="text-sm font-normal leading-[18px] text-neutral-700">
                                            {t('overview__filter_billed', { ns: 'dashboard' })}
                                        </div>
                                    </DataStatBlock.Body>
                                    <DataStatBlock.Footer>
                                        {!!monthlyRevenue && (
                                            <span>
                                                <CurrencyOrEmpty>
                                                    {monthlyRevenue.revenue_last_period}
                                                </CurrencyOrEmpty>
                                                {` `}
                                                <DateText
                                                    format={{
                                                        year: 'numeric',
                                                        month: 'short',
                                                    }}
                                                >
                                                    {aDateWithTheYearLastYear}
                                                </DateText>
                                            </span>
                                        )}
                                    </DataStatBlock.Footer>
                                </div>
                                <div className="relative  mx-auto px-2 pb-1" ref={progressContinerRef}>
                                    {monthlyProgressPercentage !== undefined && (
                                        <>
                                            <div className="-mt-3 xl:-mt-2">
                                                <ProgressSemiCircleChart
                                                    progress={monthlyProgressPercentage}
                                                    width={progressWidth}
                                                    height={progressHeight}
                                                />
                                            </div>
                                            <div className="absolute bottom-0 left-1/2  w-full -translate-x-1/2 translate-y-[6px] transform text-center text-xs font-normal text-neutral-500">
                                                {t('chart_label__last_year', { ns: 'dashboard' })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="m-0!">
                                <ShowMoreArrow />
                            </div>
                        </DataStatBlock.Item>
                    </Link>
                </DataStatBlock>
            )}

            {showRevenueChart && (
                <DataStatBlock>
                    <div className="overflow-hidden md:flex-3">
                        <ExplorerViewChart
                            onReady={() => {
                                setIsDoneTopOfFold(true);
                            }}
                        />
                    </div>
                    <div className="h-auto md:flex-2">
                        <DashboardTaskDataBlockItem />
                    </div>
                </DataStatBlock>
            )}

            {!showRevenueChart && (
                <div style={{ minHeight: '430px' }}>
                    <DashboardTaskDataBlockItem />
                </div>
            )}

            <DashboardCustomersSection enabled={isDoneTopOfFold} />

            <div>
                {(canReadOpenOrders || canReadQuotes) && (
                    <TabsLegacy
                        onTabChange={(tabId) => {
                            sendEvent('home_tab_changed', {
                                tab: tabId === '0' ? 'open_orders' : 'open_quotes',
                            });
                        }}
                    >
                        {canReadOpenOrders && (
                            <TabsLegacyItem
                                tabId="0"
                                label={t('tab_title_order_overview__open_orders', { ns: 'dashboard' })}
                            >
                                <TabContentTableWrapper>
                                    <DataTable
                                        emptyLabel={t('empty_table__no_results')}
                                        isLoading={isLoadingQuotes}
                                        data={ordersSnippet}
                                        columns={orderColumns}
                                        manualSorting
                                        sorting={orderSorting}
                                        onSortingChange={setOrderSorting}
                                    />
                                    <TableFooter
                                        loading={isLoadingOrdersCount}
                                        count={ordersAggregates?.count ?? 0}
                                        onAction={() => {
                                            goToOpenOrders();
                                        }}
                                    />
                                </TabContentTableWrapper>
                            </TabsLegacyItem>
                        )}
                        {canReadQuotes && (
                            <TabsLegacyItem
                                tabId="1"
                                label={t('tab_title_order_overview__open_quotes', { ns: 'dashboard' })}
                            >
                                <TabContentTableWrapper>
                                    <DataTable
                                        emptyLabel={t('empty_table__no_results')}
                                        isLoading={isLoadingQuotes}
                                        data={quotesSnippet}
                                        columns={quoteColumns}
                                        manualSorting
                                        sorting={quoteSorting}
                                        onSortingChange={setQuoteSorting}
                                    />
                                    <TableFooter
                                        loading={isLoadingQuotesCount}
                                        count={quotesAggregates?.count ?? 0}
                                        onAction={() => {
                                            goToOpenQuotes();
                                        }}
                                    />
                                </TabContentTableWrapper>
                            </TabsLegacyItem>
                        )}
                    </TabsLegacy>
                )}
                <div className="h-16" />
            </div>
        </Page>
    );
}

export default HomePage;
